/**
 * Platform — Tenant Applications inbox (Sprint D).
 *
 * Route: /platform/tenants/pending
 *
 * Super-Admin only. Tabs for: pending / approved / rejected / archived.
 * Each row exposes Approve / Reject / Archive actions per the lifecycle.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import {
  ShieldAlert, Lock, RefreshCw, Check, XCircle, Archive,
  Sparkles, Phone, Mail, Globe,
} from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const TABS = [
  { id: "pending_approval", ar: "بانتظار الموافقة", en: "Pending",    countKey: "pending_approval" },
  { id: "approved",          ar: "معتمدة",          en: "Approved",   countKey: null },
  { id: "rejected",          ar: "مرفوضة",          en: "Rejected",   countKey: null },
  { id: "archived",          ar: "مؤرشفة",          en: "Archived",   countKey: null },
  { id: "waitlisted",        ar: "قائمة الانتظار",  en: "Waitlisted", countKey: "waitlisted" },
];

export default function PlatformTenantsPending() {
  const { user, loading: authLoading } = useAuth();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const allowed = user && user.role === "admin";
  const isAR = (document?.documentElement?.lang || "ar").startsWith("ar");
  const t = (en, ar) => (isAR ? ar : en);

  const [tab, setTab] = useState("pending_approval");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [counts, setCounts] = useState({ pending_approval: null, waitlisted: null });

  const loadCounts = async () => {
    try {
      const [p, w] = await Promise.all([
        api.get("/platform/tenants/applications?status=pending_approval&limit=500"),
        api.get("/platform/tenants/applications?status=waitlisted&limit=500"),
      ]);
      setCounts({
        pending_approval: p.data.count ?? (p.data.items || []).length,
        waitlisted: w.data.count ?? (w.data.items || []).length,
      });
    } catch { /* badge is best-effort */ }
  };

  const load = async (which = tab) => {
    setLoading(true);
    try {
      const url = which === "pending_approval"
        ? "/platform/tenants/pending"
        : `/platform/tenants/applications?status=${which}`;
      const r = await api.get(url);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
    loadCounts();
  };

  useEffect(() => { if (allowed) load(tab); /* eslint-disable-next-line */ }, [tab, allowed]);

  const act = async (app_id, action, extra) => {
    if (action === "reject") {
      const reason = window.prompt(t("Reason for rejection (visible internally)", "سبب الرفض (داخلي فقط)"));
      if (!reason) return;
      extra = { reason };
    } else if (action === "approve") {
      const ok = await confirm({
        title: t("Approve this salon and create a real tenant?", "اعتماد هذا الصالون وإنشاء tenant فعلي؟"),
        confirmLabel: t("Approve", "اعتماد"),
        cancelLabel: t("Cancel", "إلغاء"),
        variant: "danger",
      });
      if (!ok) return;
    } else if (action === "archive") {
      const ok = await confirm({
        title: t("Archive this application?", "أرشفة هذا الطلب؟"),
        confirmLabel: t("Archive", "أرشفة"),
        cancelLabel: t("Cancel", "إلغاء"),
        variant: "danger",
      });
      if (!ok) return;
    }
    setBusy(app_id);
    try {
      await api.post(`/platform/tenants/${app_id}/${action}`, extra || {}, {
        headers: { "X-Confirm-Action": "true" },
      });
      toast.success(t("Done", "تم"));
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  if (authLoading) return <div className="p-8 text-center text-stone-400">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!allowed) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="mx-auto text-rose-500 mb-3" size={36} />
          <h2 className="text-xl font-bold text-stone-800">{t("Super-Admin only", "للسوبر أدمن فقط")}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="platform-tenants-pending">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500">
            <ShieldAlert size={14} className="text-[#7A1F3D]" /> {t("Platform · Tenant applications", "المنصة · طلبات الـ tenants")}
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mt-1">{t("Tenant applications inbox", "صندوق طلبات الـ Tenants")}</h1>
          <p className="text-sm text-stone-500 mt-1">
            {t("Approve, reject or archive signup applications. Real tenant is created only on approval.", "اعتمد أو ارفض أو أرشف الطلبات. الـ tenant الفعلي يُنشأ فقط عند الاعتماد.")}
          </p>
        </div>
        <button onClick={() => load(tab)} className="px-3 py-1.5 text-sm border border-stone-200 rounded-lg hover:border-[#7A1F3D] inline-flex items-center gap-1.5" data-testid="apps-refresh">
          <RefreshCw size={14} /> {t("Refresh", "تحديث")}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="apps-tabs">
        {TABS.map((s) => {
          const c = s.countKey ? counts[s.countKey] : null;
          return (
            <button
              key={s.id} onClick={() => setTab(s.id)} data-testid={`apps-tab-${s.id}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition inline-flex items-center gap-1.5 ${
                tab === s.id ? "bg-[#1a1015] text-white border-[#1a1015]" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
              }`}
            >
              <span>{isAR ? s.ar : s.en}</span>
              {typeof c === "number" && (
                <span
                  data-testid={`apps-count-${s.id}`}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                    tab === s.id
                      ? "bg-white/15 text-white"
                      : (c > 0 ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-500")
                  }`}
                >
                  {c}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
        {loading && <div className="p-6 text-center text-stone-400 text-sm">{t("Loading...", "جاري التحميل...")}</div>}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-stone-400 text-sm">{t("No applications in this bucket.", "لا توجد طلبات في هذه الحالة.")}</div>
        )}
        {!loading && items.map((a) => (
          <div key={a.application_id} className="p-4 sm:p-5" data-testid={`app-row-${a.application_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Sparkles size={14} className="text-[#7A1F3D]" />
                  <span className="font-bold text-stone-800">{a.business_name?.en || a.business_name?.ar}</span>
                  {a.business_name?.ar && a.business_name?.en && (
                    <span className="text-stone-500 text-sm" dir="rtl">· {a.business_name.ar}</span>
                  )}
                  <code className="text-[10px] text-stone-500">{a.application_id}</code>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 font-mono">{a.vertical}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 inline-flex items-center gap-1"><Globe size={9} /> {a.country}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-stone-600">
                  <span className="inline-flex items-center gap-1"><Mail size={11} /> {a.email}</span>
                  <span className="inline-flex items-center gap-1" dir="ltr"><Phone size={11} /> {a.phone}</span>
                  <span className="text-stone-400">· {(a.submitted_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                {a.tenant_id && (
                  <div className="mt-1.5 text-[11px] text-emerald-700">
                    {t("Approved as", "اعتُمد كـ")} <code className="font-mono">{a.tenant_id}</code>
                    {a.reviewed_by && <span className="text-stone-400"> · by {a.reviewed_by}</span>}
                  </div>
                )}
                {a.reject_reason && (
                  <div className="mt-1.5 text-[11px] text-rose-700">
                    {t("Reason", "السبب")}: <span className="text-stone-700">{a.reject_reason}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                {a.status === "pending_approval" && (
                  <>
                    <button onClick={() => act(a.application_id, "approve")} disabled={busy === a.application_id} data-testid={`approve-${a.application_id}`}
                      className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50">
                      <Check size={12} /> {t("Approve", "اعتماد")}
                    </button>
                    <button onClick={() => act(a.application_id, "reject")} disabled={busy === a.application_id} data-testid={`reject-${a.application_id}`}
                      className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50">
                      <XCircle size={12} /> {t("Reject", "رفض")}
                    </button>
                  </>
                )}
                {a.status !== "approved" && a.status !== "archived" && (
                  <button onClick={() => act(a.application_id, "archive")} disabled={busy === a.application_id} data-testid={`archive-${a.application_id}`}
                    className="px-3 py-1.5 rounded-lg bg-stone-400 hover:bg-stone-500 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50">
                    <Archive size={12} /> {t("Archive", "أرشفة")}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <ConfirmDialogSlot />
    </div>
  );
}
